/**
 * The encrypted, access-controlled offline store (PROJECT_SPEC.md §8; OFF-001, OFF-002, OFF-004).
 *
 * Access control, same standard as online (OFF-002): nothing in the store can be read without the operator's
 * device PIN, a wrong PIN is counted on disk (so closing the page does not reset the count), and after
 * MAX_PIN_ATTEMPTS the store stays locked until the operator re-proves their identity to the identity provider
 * online. Nothing is ever deleted by a lockout: the records are evidence, and a lockout that destroyed them
 * would let anyone erase a day's batch record by guessing wrong.
 *
 * Attribution without the IdP (OFF-004): unlocking with the PIN is evidence of who was at the device, tied to
 * the person who enrolled it. It is NOT a credential the server can verify, so the server never relies on it:
 * on sync the person must authenticate to the IdP, and a capture is accepted only when that person is the one
 * who made it (N6). The PIN is never sent anywhere.
 */
import {
  type CommandQueue,
  type OfflineAnchor,
  type QueuedCommand,
  type WorkingSet,
} from "@corpex-ebr/domain";
import {
  checkMatches,
  deriveKey,
  KDF_ITERATIONS,
  makeCheck,
  newSalt,
  open,
  type Sealed,
  seal,
} from "./crypto.js";
import type { KvStore } from "./kv.js";

export const MAX_PIN_ATTEMPTS = 5;
export const MIN_PIN_LENGTH = 6;
const META = "meta";
const ANCHOR = "anchor";
const CMD = "cmd:";
const WSET = "wset";
const WIPED = "wiped";

interface Meta {
  salt: string;
  check: Sealed;
  userId: string;
  deviceId: string;
  failedAttempts: number;
  iterations: number;
}

export type VaultStatus = "NOT_ENROLLED" | "LOCKED" | "LOCKED_OUT" | "UNLOCKED";

export type UnlockResult =
  | { ok: true }
  | { ok: false; reason: "WRONG_PIN"; attemptsLeft: number }
  | { ok: false; reason: "LOCKED_OUT" | "NOT_ENROLLED" };

export interface StoredAnchor extends OfflineAnchor {
  readonly maxOfflineMs: number;
}

export interface WipeNotice {
  readonly at: string;
  readonly reason: string;
  readonly commandsDiscarded: number;
}

export class Vault {
  private key: CryptoKey | null = null;

  constructor(
    private readonly kv: KvStore,
    private readonly opts: { iterations?: number } = {},
  ) {}

  private meta = async (): Promise<Meta | undefined> => (await this.kv.get(META)) as Meta | undefined;

  async status(): Promise<VaultStatus> {
    const m = await this.meta();
    if (!m) return "NOT_ENROLLED";
    if (this.key) return "UNLOCKED";
    return m.failedAttempts >= MAX_PIN_ATTEMPTS ? "LOCKED_OUT" : "LOCKED";
  }

  async identity(): Promise<{ userId: string; deviceId: string } | null> {
    const m = await this.meta();
    return m ? { userId: m.userId, deviceId: m.deviceId } : null;
  }

  /** First use on this device: the signed-in person sets the PIN that will seal everything they queue. */
  async enroll(pin: string, userId: string, deviceId: string): Promise<void> {
    if (await this.meta()) throw new Error("This device is already enrolled.");
    if (pin.length < MIN_PIN_LENGTH)
      throw new Error(`The device PIN needs at least ${MIN_PIN_LENGTH} characters.`);
    const iterations = this.opts.iterations ?? KDF_ITERATIONS;
    const salt = newSalt();
    const key = await deriveKey(pin, salt, iterations);
    await this.kv.put(META, {
      salt,
      check: await makeCheck(key),
      userId,
      deviceId,
      failedAttempts: 0,
      iterations,
    } satisfies Meta);
    await this.kv.remove(WIPED);
    this.key = key;
  }

  async unlock(pin: string): Promise<UnlockResult> {
    const m = await this.meta();
    if (!m) return { ok: false, reason: "NOT_ENROLLED" };
    if (m.failedAttempts >= MAX_PIN_ATTEMPTS) return { ok: false, reason: "LOCKED_OUT" };
    const key = await deriveKey(pin, m.salt, m.iterations);
    if (await checkMatches(key, m.check)) {
      await this.kv.put(META, { ...m, failedAttempts: 0 });
      this.key = key;
      return { ok: true };
    }
    // counted before anything else can happen, and on disk: closing the page does not reset it
    const failedAttempts = m.failedAttempts + 1;
    await this.kv.put(META, { ...m, failedAttempts });
    return failedAttempts >= MAX_PIN_ATTEMPTS
      ? { ok: false, reason: "LOCKED_OUT" }
      : { ok: false, reason: "WRONG_PIN", attemptsLeft: MAX_PIN_ATTEMPTS - failedAttempts };
  }

  lock(): void {
    this.key = null;
  }

  /** After the enrolled person has re-authenticated to the IdP online: the count starts again. Records untouched. */
  async clearLockout(signedInUserId: string): Promise<boolean> {
    const m = await this.meta();
    if (!m || m.userId !== signedInUserId) return false;
    await this.kv.put(META, { ...m, failedAttempts: 0 });
    return true;
  }

  private requireKey(): CryptoKey {
    if (!this.key) throw new Error("The offline store is locked.");
    return this.key;
  }

  async loadQueue(): Promise<CommandQueue> {
    const key = this.requireKey();
    const ids = await this.kv.keys(CMD);
    const all = await Promise.all(
      ids.map(async (id) => open<QueuedCommand>(key, (await this.kv.get(id)) as Sealed)),
    );
    return all.sort((a, b) => a.sequence - b.sequence);
  }

  /** Every record is written before the call returns, so a crash after it loses nothing (OFF-001). */
  async saveQueue(queue: CommandQueue): Promise<void> {
    const key = this.requireKey();
    const keep = new Set(queue.map((c) => `${CMD}${c.clientCommandId}`));
    for (const c of queue) await this.kv.put(`${CMD}${c.clientCommandId}`, await seal(key, c));
    for (const id of await this.kv.keys(CMD)) if (!keep.has(id)) await this.kv.remove(id);
  }

  async loadAnchor(): Promise<StoredAnchor | null> {
    const sealed = (await this.kv.get(ANCHOR)) as Sealed | undefined;
    return sealed ? open<StoredAnchor>(this.requireKey(), sealed) : null;
  }

  async saveAnchor(a: StoredAnchor): Promise<void> {
    await this.kv.put(ANCHOR, await seal(this.requireKey(), a));
  }

  /**
   * URS-8.6-001: the read replica (open batches, MBR snapshots, lots, equipment). It is sealed under the same
   * PIN-derived key as the queue: an MBR and a day's batch data are confidential, and a lost tablet must not
   * hand them over. IndexedDB holds it (see DEC-OFF-001): one sealed record, replaced whole on each refresh.
   */
  async loadWorkingSet(): Promise<WorkingSet | null> {
    const sealed = (await this.kv.get(WSET)) as Sealed | undefined;
    return sealed ? open<WorkingSet>(this.requireKey(), sealed) : null;
  }

  async saveWorkingSet(ws: WorkingSet): Promise<void> {
    await this.kv.put(WSET, await seal(this.requireKey(), ws));
  }

  /**
   * The server has said this device was reported lost: whoever holds it can add nothing to the record, so the
   * copy of unsynced data on it is removed. What was discarded is kept as a plain notice — never silently.
   */
  async wipe(reason: string, now: string): Promise<WipeNotice> {
    const ids = await this.kv.keys(CMD);
    for (const id of ids) await this.kv.remove(id);
    await this.kv.remove(ANCHOR);
    await this.kv.remove(WSET);
    await this.kv.remove(META);
    this.key = null;
    const notice: WipeNotice = { at: now, reason, commandsDiscarded: ids.length };
    await this.kv.put(WIPED, notice);
    return notice;
  }

  async wipeNotice(): Promise<WipeNotice | null> {
    return ((await this.kv.get(WIPED)) as WipeNotice | undefined) ?? null;
  }
}
